import { useEffect, useState } from 'react';
import type { ListadoOrigen, ResultadoCargue, ResultadoEnvio } from '@reembolsables/shared';
import { copy } from '../../copy/es';
import { api, ErrorApi, textoDeError } from '../../lib/api';
import { useSesion } from '../sesion';
import { PeriodoSelect } from '../PeriodoSelect';
import { fechaHoraLarga, nombrePeriodo, periodoActual } from '../../lib/formato';

interface EstadoPublicacion {
  id: string;
  periodo: string;
  estado: 'pendiente' | 'validando' | 'generando' | 'enviando' | 'completada' | 'fallida';
  progreso: {
    centros_total?: number;
    centros_listos?: number;
    correos_total?: number;
    correos_enviados?: number;
  };
  solicitadaEn: string;
  terminadaEn: string | null;
  error_negocio: string | null;
  documentos_faltantes: {
    cantidad: number;
    lista: { centroCosto: string; tipo: string; archivo: string; desde: string | null }[];
  };
}

/** The screen the mockup does not cover (publication with state and progress), in the same visual system. */
export function PublicacionPage() {
  const { usuario } = useSesion();
  const c = copy.admin.publicacion;
  const [periodo, setPeriodo] = useState(periodoActual());
  const [estado, setEstado] = useState<EstadoPublicacion | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [origen, setOrigen] = useState<ListadoOrigen | null>(null);
  const [listado, setListado] = useState<ResultadoCargue | null>(null);
  const drive = origen?.origen === 'drive';
  const activa =
    estado && ['pendiente', 'validando', 'generando', 'enviando'].includes(estado.estado);

  async function consultar(p = periodo) {
    try {
      setEstado(await api<EstadoPublicacion>(`/api/admin/periodos/${p}/publicacion`));
    } catch (e) {
      if (e instanceof ErrorApi && e.estado === 404) setEstado(null);
      else setError(textoDeError(e));
    }
  }
  useEffect(() => {
    void consultar(periodo);
  }, [periodo]);
  useEffect(() => {
    api<ListadoOrigen>('/api/admin/listados/origen')
      .then(setOrigen)
      .catch(() => setOrigen(null));
  }, []);
  useEffect(() => {
    if (!activa) return;
    const t = setInterval(() => void consultar(), 3000);
    return () => clearInterval(t);
  }, [activa, periodo]);

  async function publicar() {
    setError(null);
    setEnviando(true);
    try {
      await api(`/api/admin/periodos/${periodo}/publicacion`, { method: 'POST' });
      await consultar();
    } catch (e) {
      setError(textoDeError(e));
    } finally {
      setEnviando(false);
    }
  }

  /** The single button: read the listing from the shared folder, replace it and publish. */
  async function enviar() {
    setError(null);
    setListado(null);
    setEnviando(true);
    try {
      const r = await api<ResultadoEnvio>(`/api/admin/periodos/${periodo}/envio`, {
        method: 'POST',
      });
      setListado(r.listado);
      await consultar();
    } catch (e) {
      if (e instanceof ErrorApi && e.estado === 422 && (e.cuerpo as ResultadoEnvio)?.listado)
        setListado((e.cuerpo as ResultadoEnvio).listado);
      else setError(textoDeError(e));
    } finally {
      setEnviando(false);
    }
  }
  const pct = (a?: number, b?: number) => (b ? Math.round(((a ?? 0) / b) * 100) : 0);

  return (
    <div>
      <h2>
        {c.titulo} · {nombrePeriodo(periodo).toLowerCase()}
      </h2>
      <p className="sub">{c.subtitulo}</p>
      <PeriodoSelect id="periodo-pub" etiqueta={c.periodo} valor={periodo} onChange={setPeriodo} />
      {drive && origen.origen === 'drive' && (
        <div className="ctx" role="note" aria-label={c.drive.titulo}>
          <div>
            <div className="k">{c.drive.archivo}</div>
            <div className="v">{origen.archivo?.nombre ?? c.drive.sinArchivo}</div>
          </div>
          {origen.archivo && (
            <>
              <div>
                <div className="k">{c.drive.modificado}</div>
                <div className="v">{fechaHoraLarga(origen.archivo.modificadoEn)}</div>
              </div>
              <div>
                <div className="k">{c.drive.por}</div>
                <div className="v">{origen.archivo.modificadoPor ?? '–'}</div>
              </div>
            </>
          )}
          <div>
            <div className="k">{c.drive.titulo}</div>
            <div className="v">
              <a href={origen.carpetaUrl} target="_blank" rel="noreferrer">
                {c.drive.carpeta}
              </a>
            </div>
          </div>
          {origen.error && (
            <div className="ok-box bad" role="alert">
              {origen.error}
            </div>
          )}
        </div>
      )}
      {drive && <p className="sub">{c.drive.nota}</p>}
      {usuario?.rol === 'operador' && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 18 }}>
          <button
            className="cta amber"
            disabled={enviando || Boolean(activa)}
            onClick={() => void (drive ? enviar() : publicar())}
          >
            {drive
              ? estado?.estado === 'completada'
                ? c.reenviar
                : c.enviar
              : estado?.estado === 'completada'
                ? c.republicar
                : c.publicar}
          </button>
        </div>
      )}
      {listado && (
        <div className={`ok-box ${listado.resultado === 'aceptado' ? '' : 'bad'}`} role="status">
          <b>
            {listado.resultado === 'aceptado' ? c.drive.listadoAceptado : c.drive.listadoRechazado}
          </b>
          {listado.resultado === 'aceptado' && (
            <>
              {' '}
              · {listado.centros_costo} centros de costo · {listado.destinatarios} correos
            </>
          )}
          {listado.errores.map((e, i) => (
            <div key={i}>{e.mensaje}</div>
          ))}
          {listado.advertencias.map((a, i) => (
            <div key={i} style={{ color: '#8a4f0d' }}>
              {a.mensaje}
            </div>
          ))}
        </div>
      )}
      {error && (
        <div className="ok-box bad" role="alert">
          {error}
        </div>
      )}

      {estado && (
        <div role="status">
          <div className="ctx">
            <div>
              <div className="k">{c.estadoEtiqueta}</div>
              <div className="v">{c.estados[estado.estado]}</div>
            </div>
            <div>
              <div className="k">{c.paquetes}</div>
              <div className="v">
                {estado.progreso.centros_listos ?? 0} / {estado.progreso.centros_total ?? '–'}
              </div>
            </div>
            <div>
              <div className="k">{c.correos}</div>
              <div className="v">
                {estado.progreso.correos_enviados ?? 0} / {estado.progreso.correos_total ?? '–'}
              </div>
            </div>
          </div>
          <div className="progreso">
            <div className="lbl">
              <span>{c.paquetes}</span>
              <span>{pct(estado.progreso.centros_listos, estado.progreso.centros_total)}%</span>
            </div>
            <div className="track">
              <div
                className="fill"
                style={{
                  width: `${pct(estado.progreso.centros_listos, estado.progreso.centros_total)}%`,
                }}
              />
            </div>
          </div>
          <div className="progreso">
            <div className="lbl">
              <span>{c.correos}</span>
              <span>{pct(estado.progreso.correos_enviados, estado.progreso.correos_total)}%</span>
            </div>
            <div className="track">
              <div
                className="fill"
                style={{
                  width: `${pct(estado.progreso.correos_enviados, estado.progreso.correos_total)}%`,
                }}
              />
            </div>
          </div>
          {estado.error_negocio && (
            <div className="ok-box bad" role="alert">
              {estado.error_negocio}
            </div>
          )}
          {estado.estado === 'completada' && (
            <div className="ok-box">
              <b>{c.completada}</b>
            </div>
          )}
          {estado.documentos_faltantes?.cantidad > 0 && (
            <div className="ok-box warn" role="alert">
              <b>{c.faltantes(estado.documentos_faltantes.cantidad)}</b>
              <ul style={{ margin: '8px 0 0 18px', maxHeight: 180, overflowY: 'auto' }}>
                {estado.documentos_faltantes.lista.map((f, i) => (
                  <li key={i}>
                    <b>{f.centroCosto}</b> · {f.tipo} · {f.archivo}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="expira">{c.nota}</div>
        </div>
      )}
    </div>
  );
}
